/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { type ComponentProps } from 'react';
import classnames from 'classnames';
import Choices from './Choices/Choices';

const DEFAULT_ON_CHANGE = () => {};

type FieldValue = string | number | (string | number)[] | boolean;

interface FieldProps extends Omit<
  ComponentProps<typeof Choices>,
  'id' | 'value' | 'onChange'
> {
  id: string;
  type?: string;
  label: string;
  value?: FieldValue;
  error?: string | null;
  required?: boolean;
  onChange?: (event: { target: { value: FieldValue } }) => void;
}

/**
 * This is the Field component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Field({
  id,
  type = 'text',
  label,
  value = '',
  error = null,
  required = false,
  onChange = DEFAULT_ON_CHANGE,
  ...props
}: FieldProps) {
  function renderError() {
    if (!error) return null;

    return (
      <span className="error" aria-live="polite">
        {error}
      </span>
    );
  }

  function renderInput() {
    switch (type) {
      case 'choices':
        return (
          <Choices
            id={id}
            value={value as ComponentProps<typeof Choices>['value']}
            required={required}
            {...props}
            onChange={onChange}
          />
        );
      case 'checkbox':
        return (
          <input
            id={id}
            type={type}
            required={required}
            checked={value as boolean}
            {...props}
            onChange={e => {
              onChange({
                target: {
                  value: e.target.checked,
                },
              });
            }}
          />
        );
      default:
        return (
          <input
            id={id}
            type={type}
            value={value as string | number | string[]}
            required={required}
            {...props}
            onChange={onChange}
          />
        );
    }
  }

  function renderRow() {
    if (type === 'checkbox' || type === 'radio') {
      return (
        <div
          className={`field-input field-type-${type} ${type} field-name-${id}`}
        >
          {renderInput()}
          <label
            className={classnames({ required })}
            htmlFor={id}
            dangerouslySetInnerHTML={{ __html: label }}
          />
          {renderError()}
        </div>
      );
    }

    if (type === 'choices') {
      return (
        <fieldset>
          <legend className={classnames({ required })}>{label}</legend>
          <div className={`field-input field-type-${type} field-name-${id}`}>
            {renderInput()}
            {renderError()}
          </div>
        </fieldset>
      );
    }

    return (
      <>
        <label className={classnames({ required })} htmlFor={id}>
          {label}
        </label>
        <div className={`field-input field-type-${type} field-name-${id}`}>
          {renderInput()}
          {renderError()}
        </div>
      </>
    );
  }

  return (
    <div
      key={id}
      className={classnames('field-row', `field-row-${type} field-name-${id}`, {
        'with-error': !!error,
      })}
    >
      {renderRow()}
    </div>
  );
}

export default Field;
